//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import { pxStrToNum } from '@utils/styleUtils';

interface Props {
  className?: string;
  height?: string;
  left?: React.ReactChild;
  right?: React.ReactChild;
}

// All page headers have a 40px tall content area
export const DEFAULT_PAGE_HEADER_HEIGHT = 40;
const DEFAULT_PAGE_HEADER_TOP_PADDING = theme.spacings[3];
export const DEFAULT_PAGE_HEADER_TOTAL_HEIGHT =
  pxStrToNum(DEFAULT_PAGE_HEADER_TOP_PADDING) * 2 + DEFAULT_PAGE_HEADER_HEIGHT;

/**
 * The PageHeader organism is used to render a standard page header
 * above a page's content.
 *
 * @param left The children to render in the left slot of the component (usually a PageTitle)
 * @param right The children to render in the right slot of the component (usually a set of buttons)
 */
export const PageHeader = ({
  className,
  height = `${DEFAULT_PAGE_HEADER_HEIGHT}px`,
  left,
  right,
}: Props) => {
  return (
    <PageHeaderContainer className={className}>
      <LeftChildContainer height={height}>{left}</LeftChildContainer>
      <RightChildContainer height={height}>{right}</RightChildContainer>
    </PageHeaderContainer>
  );
};

const PageHeaderContainer = styled.div(
  ({ theme }) => `
  /* Use a box-shadow rather than a border so that the height of the header is unchanged */
  box-shadow: inset 0 -1px 0 ${theme.colors.gray2};
  background-color: ${theme.colors.white};
  padding: ${DEFAULT_PAGE_HEADER_TOP_PADDING} ${theme.spacings[4]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  `,
);

const ChildContainer = styled.div<{ height: string }>(
  ({ height }) => `
    display: flex;
    align-items: center;
    height: ${height};
    `,
);

const LeftChildContainer = styled(ChildContainer)`
  flex: 1;
`;

const RightChildContainer = styled(ChildContainer)`
  justify-content: flex-end;
`;

export default PageHeader;
